import {Component, EventEmitter, Input, OnInit, OnDestroy, Output} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import last from 'lodash/last';
import find from 'lodash/find';
import { SubmissionService } from '../../../../../core/services/management/submission/submission.service';
import { RolesService } from '../../../../../core/services/roles/roles.service';
import { SharedService } from '../../../../../core/services/store/shared-store/shared-storage.service';
import { FlowManagerService } from '../../../../../modules/flow/services/flow-manager.service';
import { Submission, ChangeLog, SubmissionApproval, SubmissionApprovalRequest, SubmissionAction } from '../../../../../shared/models/flow-manager-core';
import { swalMini } from '../../../../../shared/utils/utils';
import { AfterContentChecked } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { deployModalAction, completeDeployModalAction, deployModalSuccessAction } from '../../../../../core/state/actions/entity-config.action';
import { SubmissionsApprovalRequestConfig } from '../../../../../shared/models/confirmModalConfig';
import { takeUntil } from "rxjs/operators";
import { MatTabGroup, MatTab, MatTabLabel } from '@angular/material/tabs';
import { LoadingIconComponent } from '../../../../../shared/components/loading-icon/loading-icon.component';
import { NgTemplateOutlet, AsyncPipe, DatePipe } from '@angular/common';
import { ɵEmptyOutletComponent } from '@angular/router';
import { SavingInProgressComponent } from '../../../../../shared/components/saving-in-progress/saving-in-progress.component';

type ApproveType = 'delete' | 'deploy';

@Component({
    selector: 'app-approval-details',
    templateUrl: './approval-details.component.html',
    styleUrls: ['./approval-details.component.scss'],
    imports: [MatTabGroup, MatTab, MatTabLabel, LoadingIconComponent, NgTemplateOutlet, ɵEmptyOutletComponent, SavingInProgressComponent, AsyncPipe, DatePipe]
})
export class ApprovalDetailsComponent implements OnInit, OnDestroy, AfterContentChecked {

  @Input() submission: Submission;
  @Input() changeLog: Observable<ChangeLog[]>;
  @Input() submissionApproval: Observable<SubmissionApproval>;
  @Input() approvalsSelected: string;
  @Output() clear: EventEmitter<number> = new EventEmitter<number>();
  private destroy$ = new Subject<void>();

  constructor(
    public rolesService: RolesService,
    public sharedService: SharedService,
    private submissionService: SubmissionService,
    private flowManagerService: FlowManagerService,
    private store: Store,
    private actions$: Actions
  ) {}

  currentSubmission: Submission;
  savingInProgress: Boolean = false;
  savingInProgressMessage: string;
  entityList: any = [];
  private approveType: ApproveType;
  clientId: string = '';

  ngOnInit() {
    this.entityList = this.sharedService.getEntityList;
    this.getClient();

    this.actions$.pipe(
      ofType(deployModalSuccessAction),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.savingInProgress = false;
      this.store.dispatch(completeDeployModalAction({ completed: true }));
      this.flowManagerService.getFlows(true).subscribe((flows) => {
        this.sharedService.updateFlows(flows);
      });
      this.clear.emit();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterContentChecked() {
    if (this.submission) {
      if (this.currentSubmission && this.currentSubmission.id !== this.submission.id) {
        this.hideConfirm();
      }
      this.currentSubmission = this.submission;
    }

    if (!this.clientId) {
      this.getClient();
    }
  }

  requiredApprovers(approvers: any) {
    const required: number = approvers.required;
    const approvedCount: number = approvers.approvedCount;
    let requiredApprovers = 0;

    if (required != undefined && approvedCount != undefined) {
      requiredApprovers = required - approvedCount;
      if (requiredApprovers < 0) {
        requiredApprovers = 0;
      }
    }

    return Array(requiredApprovers).fill(1);
  }

  deployToProduction() {
    const submissionApproval: SubmissionApprovalRequest = {
      submissionId: this.submission.id,
      reviewerType: this.getRole(),
      submissionType: this.submission.submissionType,
      action: 'DEPLOY',
    };
    
    this.savingInProgress = true;
    this.savingInProgressMessage =
      this.submission.submissionType === 'DELETE'
        ? 'Deleting in progress...'
        : 'Deploying in progress...';

    const payload: SubmissionsApprovalRequestConfig = {
      flowName: this.submission.flowPath,
      env: 'dev',
      submissionsApprovalRequest: submissionApproval
    };

    this.store.dispatch(deployModalAction({ payload }));
  }

  actionSubmission(action: SubmissionAction) {
    const submissionApproval: SubmissionApprovalRequest = {
      submissionId: this.submission.id,
      reviewerType: this.getRole(),
      action: action,
      submissionType: this.submission.getSubmissionType
        ? this.submission.getSubmissionType
        : 'PROMOTE',
    };

    this.savingInProgressMessage = `${
      action === 'APPROVE' ? 'Approval ' : 'Declining'
    } in progress...`;
    this.savingInProgress = true;
    this.submissionService.actionSubmission(submissionApproval).subscribe(
      (result) => {
        this.savingInProgress = false;
        const pathArr = this.submission.flowPath.split('/');
        if (!result) {
          swalMini({
            type: 'error',
            title: `Something went wrong in ${action} the Flow: ${last(pathArr)}`,
          });
        } else {
          swalMini({
            type: 'success',
            title: `Successfully ${action} the Flow:  ${last(pathArr)}`,
          });
          this.flowManagerService.getFlows(true).subscribe((flows) => {
            this.sharedService.updateFlows(flows);
          });
          this.clear.emit(this.submission.id);
        }
      },
      () => {
        swalMini({
          type: 'warning',
          title: `Problem occurred with ${action === 'APPROVE' ? ' approval' : 'decline'}`,
        });
      }
    );
  }

  getRole(): string {
    let role: string;
    if (this.rolesService.isUserInternal()) {
      role = 'INTERNAL';
    } else {
      role = 'CLIENT';
    }

    return role;
  }
  flipDown() {
    const flipButton = document.querySelector('.flip-button');
    flipButton.classList.remove('front');
    flipButton.classList.add('top');
  }

  flipUp() {
    const flipButton = document.querySelector('.flip-button');
    flipButton.classList.remove('top');
    flipButton.classList.add('front');
  }

  showConfirm(type: ApproveType) {
    this.approveType = type;
    const askButton = document.querySelector('.ask-button');
    // let confirmButton = document.querySelector('.confirm-div');
    askButton.classList.add('hide');
    // confirmButton.classList.remove('hide');
  }

  showApprove() {
    const askButton = document.querySelector('.ask-button');
    const confirmApprove = document.querySelector('.confirm-decline');
    askButton.classList.add('hide');
    confirmApprove.classList.add('hide');
  }

  showDecline() {
    const askButton = document.querySelector('.ask-button');
    const confirmApprove = document.querySelector('.confirm-approve');
    askButton.classList.add('hide');
    confirmApprove.classList.add('hide');
  }

  hideConfirm_Approve_Decline() {
    const askButton = document.querySelector('.ask-button');
    const confirmApprove = document.querySelector('.confirm-approve');
    const confirmDdecline = document.querySelector('.confirm-decline');
    askButton.classList.remove('hide');
    confirmApprove.classList.remove('hide');
    confirmDdecline.classList.remove('hide');
  }

  hideConfirm() {
    const askButton = document.querySelector('.ask-button');
    // let confirmButton = document.querySelector('.confirm-div');
    // confirmButton.classList.add('hide');
    if (askButton) {
      askButton.classList.remove('hide');
    }
  }

  getList(approvalList: Array<any>, state: string) {
    if (approvalList && state) {
      let list = [];
      if (approvalList['INTERNAL']) {
        list = list.concat(approvalList['INTERNAL']);
      }
      if (approvalList['CLIENT']) {
        list = list.concat(approvalList['CLIENT']);
      }
      const filterList = list.filter((list) => list.action === state);
      return filterList.length > 0 ? filterList : false;
    }
  }

  getClient() {
    if (this.submission) {
      const clientId = this.submission.clientId ? this.submission.clientId : this.submission.entityId;
      const client = find(this.entityList, (o) => {
        return o.key === clientId.toString();
      });

      return this.clientId = client ? client.value : '-';
    }
  }
}
